import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextArea from '@/common/components/TextArea'
import { FormTransformers } from '@/common/utils/formTransformers'
import { MaintenanceForwardingEstablishmentChoiceEntryData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useMaintenanceEstablishmentChoiceManualEntryProposalsClientReject } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/establishmentChoice/manualEntryProposals/useMaintenanceEstablishmentChoiceManualEntryProposalsClientReject'
import { maintenaceEstablishmentChoiceManualEntryProposalsClientRejectSchema, MaintenaceEstablishmentChoiceManualEntryProposalsClientRejectValidationSchema } from '@/features/maintenanceWorkflow/pages/Show/schemas/forwardings/establishmentChoice/manualEntryProposals/maintenanceEstablishmentChoiceManualEntryProposalsClientReject.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface ManualEntryProposalsClientRejectProps {
  proposal: MaintenanceForwardingEstablishmentChoiceEntryData['actual_proposal']
}

const ManualEntryProposalsClientReject = ({ proposal }: ManualEntryProposalsClientRejectProps) => {
  const { id } = useParams()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [isOpen, setIsOpen] = useState(false);

  const queryClient = useQueryClient()

  const {
    register,
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<MaintenaceEstablishmentChoiceManualEntryProposalsClientRejectValidationSchema>({
    resolver: zodResolver(maintenaceEstablishmentChoiceManualEntryProposalsClientRejectSchema),
  });

  useEffect(reset, [isOpen])

  const { mutate: clientReject, isPending: clientRejectIsPending } =
    useMaintenanceEstablishmentChoiceManualEntryProposalsClientReject({
      proposalId: proposal.id,
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflow', id] })
          queryClient.invalidateQueries({ queryKey: ['maintenanceWorkflowForwardings', id] })
          queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding'] })

          reset()
        },
        onError: (error) => {
          FormTransformers.errorsTransformer({
            setError, setMessages
          })(error)
        },
      },
    })

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="yellow"
            operationKlassToCheckPermission="Client::Maintenance::EstablishmentChoice::ManualEntryProposals::ClientReject"
          >
            Declinar
          </Button>
        )}
        useCancellableFooter
        confirmButtonClassName="rounded-br-lg"
        cancelButtonClassName="rounded-bl-lg"
        onConfirm={handleSubmit((data) => clientReject(data))}
        confirmButtonIsLoading={clientRejectIsPending}
        title="Você tem certeza?"
        subtitle="Essa ação não é reversível."
        titleClass="text-2xl"
        subtitleClassName="text-base mt-1"
        showClose={false}
        body={(
          <form className="flex flex-col gap-4">
            <TextArea
              className="text-white-800 opacity-80"
              label="Motivo da rejeição"
              {...register('client_rejected_reason')}
              markAsRequired
              errorMessage={errors.client_rejected_reason?.message || undefined}
              maxLength={512}
            />
          </form>
        )}
      />

      <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
    </>
  )
}

export default ManualEntryProposalsClientReject
